/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Badge,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { apiget, apipost } from '../../../service/api';
import { ACTIVITY_TYPES, getActivityMeta } from '../../Lead/timeline/timelineConfig';
import ActivityTimelineItem from '../../Lead/timeline/ActivityTimelineItem';
import Addnotes from '../../../components/note/AddNotes';
import Addcalls from '../../../components/call/Addcalls';
import Addemail from '../../../components/email/Addemail';
import Addmeetings from '../../../components/meeting/Addmeetings';
import AddTask from '../../../components/task/AddTask';
import ComposeEmail from '../../Lead/timeline/ComposeEmail';
import SmsPanel from '../../Lead/timeline/SmsPanel';
import CallDispositionDialog from '../../Lead/timeline/CallDispositionDialog';

const LoanActivityTimeline = ({
  encompassLoanId,
  borrowerName,
  borrowerEmail,
  borrowerPhone,
  setUserAction,
}) => {
  const [syntheticLeadId, setSyntheticLeadId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [counts, setCounts] = useState({});
  const [commPrefs, setCommPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Dialog states
  const [openNote, setOpenNote] = useState(false);
  const [openCall, setOpenCall] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openMeeting, setOpenMeeting] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [openComposeEmail, setOpenComposeEmail] = useState(false);
  const [openSms, setOpenSms] = useState(false);
  const [openCallDisposition, setOpenCallDisposition] = useState(false);

  // Build a leadData-compatible object for reused dialogs
  const leadDataCompat = {
    firstName: borrowerName?.split(' ')[0] || '',
    lastName: borrowerName?.split(' ').slice(1).join(' ') || '',
    emailAddress: borrowerEmail || '',
    phoneNumber: borrowerPhone || '',
  };

  // Get or create the mapping on mount
  useEffect(() => {
    if (!encompassLoanId) return;
    const initMapping = async () => {
      try {
        const result = await apipost('loan-timeline/mapping', {
          encompassLoanId,
          borrowerName,
          borrowerEmail,
          borrowerPhone,
        });
        if (result && result.status === 200) {
          setSyntheticLeadId(result.data.result.syntheticLeadId);
        }
      } catch (error) {
        console.error('Failed to get loan activity mapping:', error);
      }
    };
    initMapping();
  }, [encompassLoanId]);

  const fetchTimeline = useCallback(async () => {
    if (!encompassLoanId) return;
    try {
      setLoading(true);
      const result = await apiget(`loan-timeline/timeline/${encompassLoanId}`);
      if (result && result.status === 200) {
        setTimeline(result.data.timeline || []);
        setCounts(result.data.counts || {});
        setCommPrefs(result.data.commPrefs || null);
      }
    } catch (error) {
      console.error('Failed to fetch loan timeline:', error);
    } finally {
      setLoading(false);
    }
  }, [encompassLoanId]);

  useEffect(() => {
    fetchTimeline();
  }, [encompassLoanId, refreshKey]);

  // Poll for timeline updates every 30s when tab is visible
  useEffect(() => {
    const POLL_INTERVAL = 30000;
    let intervalId = null;

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(fetchTimeline, POLL_INTERVAL);
      }
    };
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchTimeline();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [encompassLoanId, fetchTimeline]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    if (setUserAction) setUserAction(Date.now());
  };

  const handleActivityCreated = () => {
    handleRefresh();
  };

  // Filter toggle
  const toggleFilter = (typeId) => {
    setActiveFilters((prev) =>
      prev.includes(typeId) ? prev.filter((f) => f !== typeId) : [...prev, typeId]
    );
  };
  const clearFilters = () => setActiveFilters([]);

  const filteredTimeline =
    activeFilters.length === 0
      ? timeline
      : timeline.filter((item) => activeFilters.includes(item.activityType));

  // Filter out status_change since loans don't have lead pipeline status changes
  const loanActivityTypes = ACTIVITY_TYPES.filter((t) => t.id !== 'status_change');

  // Quick-add buttons
  const quickAddButtons = [
    { id: 'note', label: 'Note', icon: 'mdi:note-plus-outline', onClick: () => setOpenNote(true) },
    { id: 'call', label: 'Call', icon: 'mdi:phone-plus', onClick: () => setOpenCallDisposition(true) },
    { id: 'email', label: 'Email', icon: 'mdi:email-plus-outline', onClick: () => setOpenComposeEmail(true) },
    { id: 'meeting', label: 'Meeting', icon: 'mdi:calendar-plus', onClick: () => setOpenMeeting(true) },
    { id: 'task', label: 'Task', icon: 'mdi:checkbox-marked-circle-plus-outline', onClick: () => setOpenTask(true) },
    { id: 'sms', label: 'SMS', icon: 'mdi:message-plus-outline', onClick: () => setOpenSms(true) },
  ];

  if (loading && timeline.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <Box display="flex" justifyContent="center" alignItems="center" p={4} minHeight={300}>
          <CircularProgress size={32} />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: '8px !important' }}>
        {/* Header with quick contacts */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Activity Timeline
            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              ({counts.total || 0})
            </Typography>
          </Typography>
          <Tooltip title="Refresh timeline">
            <Button
              size="small"
              onClick={handleRefresh}
              sx={{ textTransform: 'capitalize', minWidth: 'auto' }}
            >
              <Icon icon="mdi:refresh" width={18} />
            </Button>
          </Tooltip>
        </Box>

        {/* Quick contact chips */}
        <Box display="flex" gap={0.5} mb={1.5} flexWrap="wrap">
          {borrowerPhone && (
            <Chip
              icon={<Icon icon="mdi:phone" width={14} />}
              label={borrowerPhone}
              size="small"
              onClick={() => setOpenCallDisposition(true)}
              component="a"
              href={`tel:${borrowerPhone}`}
              clickable
              variant="outlined"
              color="success"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          {borrowerEmail && (
            <Chip
              icon={<Icon icon="mdi:email" width={14} />}
              label={borrowerEmail}
              size="small"
              onClick={() => setOpenComposeEmail(true)}
              clickable
              variant="outlined"
              color="info"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {/* Borrower Communication Preferences */}
        {commPrefs && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              <Icon icon="mdi:account-cog" width={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Borrower Prefers:
              <Chip
                label={commPrefs.preferredMethod === 'both' ? 'Text & Email' : commPrefs.preferredMethod === 'text' ? 'Text' : 'Email'}
                size="small"
                color={commPrefs.preferredMethod === 'text' ? 'success' : commPrefs.preferredMethod === 'email' ? 'info' : 'primary'}
                sx={{ ml: 0.5, fontSize: '0.65rem', height: 20 }}
              />
            </Typography>
            {commPrefs.phoneNumbers?.length > 0 && (commPrefs.preferredMethod === 'text' || commPrefs.preferredMethod === 'both') && (
              <Box sx={{ mt: 0.5 }}>
                {commPrefs.phoneNumbers.map((p, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <Icon icon="mdi:cellphone" width={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                    {p.number} {p.label && `(${p.label})`} {p.isPrimary && '★'}
                  </Typography>
                ))}
              </Box>
            )}
            {commPrefs.emailAddresses?.length > 0 && (commPrefs.preferredMethod === 'email' || commPrefs.preferredMethod === 'both') && (
              <Box sx={{ mt: 0.5 }}>
                {commPrefs.emailAddresses.map((e, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <Icon icon="mdi:email-outline" width={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                    {e.address} {e.label && `(${e.label})`} {e.isPrimary && '★'}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Quick-add bar */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            flexWrap: 'wrap',
          }}
        >
          {quickAddButtons.map((btn) => {
            const meta = getActivityMeta(btn.id);
            return (
              <Button
                key={btn.id}
                variant="outlined"
                size="small"
                startIcon={<Icon icon={btn.icon} width={16} />}
                onClick={btn.onClick}
                disabled={!syntheticLeadId && btn.id !== 'email' && btn.id !== 'sms'}
                sx={{
                  textTransform: 'capitalize',
                  borderColor: 'grey.300',
                  color: meta.color,
                  fontSize: '0.7rem',
                  py: 0.3,
                  px: 1,
                  '&:hover': {
                    borderColor: meta.color,
                    bgcolor: `${meta.color}08`,
                  },
                }}
              >
                {btn.label}
              </Button>
            );
          })}
        </Box>

        {/* Filter chips */}
        <Box display="flex" gap={0.5} mb={1.5} flexWrap="wrap" alignItems="center">
          <Chip
            label="All"
            size="small"
            onClick={clearFilters}
            variant={activeFilters.length === 0 ? 'filled' : 'outlined'}
            color={activeFilters.length === 0 ? 'primary' : 'default'}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          {loanActivityTypes.map((type) => {
            const isActive = activeFilters.includes(type.id);
            const countKey = type.id === 'document_request' ? 'documentRequests' : `${type.id}s`;
            const count = counts[countKey] || 0;
            return (
              <Badge
                key={type.id}
                badgeContent={count}
                color="default"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.55rem',
                    height: 14,
                    minWidth: 14,
                    top: 2,
                    right: 2,
                  },
                }}
              >
                <Chip
                  label={type.label}
                  size="small"
                  onClick={() => toggleFilter(type.id)}
                  variant={isActive ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.7rem',
                    height: 24,
                    ...(isActive
                      ? {
                          bgcolor: `${type.color}18`,
                          color: type.color,
                          borderColor: type.color,
                        }
                      : {}),
                  }}
                />
              </Badge>
            );
          })}
        </Box>

        {/* Timeline list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pr: 0.5,
            minHeight: 200,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 2,
            },
          }}
        >
          {filteredTimeline.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                {activeFilters.length > 0
                  ? 'No activities match the selected filters.'
                  : 'No activities recorded yet. Use the buttons above to add your first activity.'}
              </Typography>
            </Box>
          ) : (
            filteredTimeline.map((item, index) => (
              <ActivityTimelineItem
                key={item._id || index}
                item={item}
                isLast={index === filteredTimeline.length - 1}
              />
            ))
          )}
        </Box>
      </CardContent>

      {/* Existing Add Dialogs — pass syntheticLeadId as _id */}
      {syntheticLeadId && (
        <>
          <Addnotes
            open={openNote}
            handleClose={() => setOpenNote(false)}
            _id={syntheticLeadId}
            setUserAction={handleActivityCreated}
            method="loan"
          />
          <Addcalls
            open={openCall}
            handleClose={() => setOpenCall(false)}
            _id={syntheticLeadId}
            setUserAction={handleActivityCreated}
          />
          <Addemail
            open={openEmail}
            handleClose={() => setOpenEmail(false)}
            _id={syntheticLeadId}
            setUserAction={handleActivityCreated}
            receiver={leadDataCompat}
          />
          <Addmeetings
            open={openMeeting}
            handleClose={() => setOpenMeeting(false)}
            _id={syntheticLeadId}
            data={leadDataCompat}
            setUserAction={handleActivityCreated}
          />
          <AddTask
            open={openTask}
            handleClose={() => setOpenTask(false)}
            _id={syntheticLeadId}
            setUserAction={handleActivityCreated}
            leadDatas={leadDataCompat}
          />
        </>
      )}

      {/* Communication Dialogs — use loan-timeline endpoints */}
      <ComposeEmail
        open={openComposeEmail}
        handleClose={() => setOpenComposeEmail(false)}
        leadId={syntheticLeadId}
        leadData={leadDataCompat}
        onSuccess={handleActivityCreated}
        apiEndpoint="loan-timeline/send-email"
        extraPayload={{ encompassLoanId }}
      />
      <SmsPanel
        open={openSms}
        handleClose={() => setOpenSms(false)}
        leadId={syntheticLeadId}
        leadData={leadDataCompat}
        onSuccess={handleActivityCreated}
        apiEndpoint="loan-timeline/send-sms"
        smsHistoryEndpoint={`loan-timeline/sms/${encompassLoanId}`}
        extraPayload={{ encompassLoanId }}
      />
      <CallDispositionDialog
        open={openCallDisposition}
        handleClose={() => setOpenCallDisposition(false)}
        leadId={syntheticLeadId}
        leadData={leadDataCompat}
        onSuccess={handleActivityCreated}
      />
    </Card>
  );
};

export default LoanActivityTimeline;
